// src/services/allauthApi.js
import axios from 'axios';
import { getBaseUrl, logger, fetchCsrfToken, getCsrfToken } from './utils';

// Client type - use 'browser' for cookie-based auth (more secure than 'app' token-based)
const CLIENT_TYPE = 'browser';

const allauthApiClient = axios.create({
  baseURL: `${getBaseUrl()}/_allauth/${CLIENT_TYPE}/v1`,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  }
});

// Add interceptor for debug logging and CSRF token handling
allauthApiClient.interceptors.request.use(async config => {
  // Reduced debug logging to minimize console noise
  // Only log non-session requests to reduce noise from polling
  if (!config.url.includes('/auth/session')) {
    logger.debug('AllAuthAPI', `${config.method.toUpperCase()} ${config.url}`);
  }
  
  // For methods that modify data, ensure we have a fresh CSRF token
  if (['post', 'put', 'patch', 'delete'].includes(config.method.toLowerCase())) {
    // Special case: for auth endpoints, fetch a fresh token first
    if (config.url.startsWith('/auth/')) {
      await fetchCsrfToken();
    }
    
    // Get the token from cookies and add it to the request headers
    const token = getCsrfToken();
    if (token) {
      config.headers['X-CSRFToken'] = token;
    }
  }
  
  return config;
});

/**
 * Get allauth configuration (authentication methods, providers, etc.)
 */
export const getConfig = async () => {
  const response = await allauthApiClient.get('/config');
  return response.data;
};

/**
 * Get current session status and authentication info
 */
export const getSession = async () => {
  try {
    const response = await allauthApiClient.get('/auth/session');
    return response.data;
  } catch (error) {
    // If we get a 401, we're not authenticated, but it's an expected response
    if (error.response?.status === 401) {
      return error.response.data;
    }
    
    // Handle 410 Gone which means the session has expired or been invalidated
    if (error.response?.status === 410) {
      logger.debug('AllAuthAPI', 'Session expired or invalidated (410 Gone)');
      
      // Return a standardized response indicating not authenticated
      return {
        status: 401,
        data: {
          flows: [
            { id: 'login' },
            { id: 'signup' }
          ]
        },
        meta: {
          is_authenticated: false
        }
      };
    }
    
    // For other errors, log and rethrow
    logger.error('AllAuthAPI', 'Error getting session:', error);
    throw error;
  }
};

/**
 * Login with email and password
 */
export const login = async (email, password) => {
  try {
    const response = await allauthApiClient.post('/auth/login', {
      email,
      password
    });
    
    return response.data;
  } catch (error) {
    // Check if we need additional authentication steps (2FA, etc.)
    if (error.response?.status === 401) {
      return error.response.data;
    }
    
    logger.error('AllAuthAPI', 'Login failed:', error);
    throw error;
  }
};

/**
 * Register a new account
 */
export const signup = async (email, password1, password2) => {
  try {
    const response = await allauthApiClient.post('/auth/signup', {
      email,
      password: password1,
      password_confirm: password2
    });
    
    return response.data;
  } catch (error) {
    // Check if we need additional authentication steps (email verification, etc.)
    if (error.response?.status === 401) {
      return error.response.data;
    }
    
    logger.error('AllAuthAPI', 'Signup failed:', error);
    throw error;
  }
};

/**
 * Logout the current user
 */
export const logout = async () => {
  try {
    const response = await allauthApiClient.delete('/auth/session');
    return { success: true };
  } catch (error) {
    // 401 is expected after logout (means we're no longer authenticated)
    if (error.response?.status === 401) {
      logger.debug('AllAuthAPI', 'Logged out successfully (401 response)');
      return { success: true };
    }
    
    logger.error('AllAuthAPI', 'Logout failed:', error);
    throw error;
  }
};

/**
 * Request a password reset
 */
export const requestPasswordReset = async (email) => {
  const response = await allauthApiClient.post('/auth/password/request', { email });
  return response.data;
};

/**
 * Reset password with a token
 */
export const resetPassword = async (key, password) => {
  const response = await allauthApiClient.post('/auth/password/reset', { 
    key, 
    password
  });
  
  return response.data;
};

/**
 * Verify email with a token
 * This method handles both new browser sessions and existing sessions
 */
export const verifyEmail = async (key) => {
  try {
    // First, make sure we have a CSRF token by fetching a fresh one
    // This is important for new browser sessions where no cookies exist yet
    try {
      await fetchCsrfToken();
    } catch (csrfError) {
      logger.debug('AllAuthAPI', 'Failed to fetch CSRF token before email verification', csrfError);
      // Continue anyway - we'll handle the error in the main try/catch
    }
    
    // Now try to verify the email
    const response = await allauthApiClient.post('/auth/email/verify', { key });
    return response.data;
  } catch (error) {
    // Very specific handling for email verification 401 errors:
    // This ONLY applies to the email verification endpoint and 401 status
    // This is to handle cross-browser verification where the email is verified
    // but the user isn't logged in due to security measures in django-allauth
    if (error.response?.status === 401 && 
        error.config?.url === '/auth/email/verify' && 
        error.config?.method === 'post') {
      
      logger.debug('AllAuthAPI', 'Email verification in different browser session - treating as success with login required');
      
      // Instead of returning a success object, throw an error with a specific message that the
      // EmailVerificationView component can detect and handle properly
      const verificationError = new Error('Email verification successful in different browser session - login required');
      verificationError.verificationStatus = {
        success: true,
        meta: {
          is_verified: true,
          is_authenticated: false
        },
        data: {
          message: "Your email has been verified successfully! You can now log in with your credentials."
        }
      };
      throw verificationError;
    }
    
    // For all other errors, log and rethrow
    logger.error('AllAuthAPI', 'Email verification failed:', error);
    throw error;
  }
};

/**
 * Complete two-factor authentication
 */
export const completeTwoFactorAuth = async (code) => {
  const response = await allauthApiClient.post('/auth/2fa/authenticate', { code });
  
  return response.data;
};

/**
 * Get information about an email verification key
 */
export const getEmailVerificationInfo = async (key) => {
  // For browser clients, key is passed in URL params
  const response = await allauthApiClient.get('/auth/email/verify', {
    params: { key }
  });
  return response.data;
};

/**
 * Get information about a password reset key
 */
export const getPasswordResetInfo = async (key) => {
  // For browser clients, key is passed in URL params
  const response = await allauthApiClient.get('/auth/password/reset', {
    params: { key }
  });
  return response.data;
};

export default allauthApiClient;